import { useState, useRef, useEffect } from "react";
import { GetChatConversation, GetChatMessage, GetOrCreateConversation } from "../../services/api";
import { socket } from "../../socket/socket";
import Conversation from "./Conversation";
import ChatWindow from "./ChatWindow";
import { useDispatch, useSelector } from "react-redux";
import { setHideBottomNav } from "../../features/ui/uiSlice";
import { setRemoteStatus, setRemoteId, setConversationId, resetUnreadByConversation } from "../../features/message/messageSlice";
import useIsMobile from "../../hooks/useIsMobile";
import { CHAT_EVENTS } from "../../socket/socketEvents";
import { useLocation } from "react-router-dom";

export default function Messages() {
  const [chats, setChats] = useState([]);
  const [chatsLoaded, setChatsLoaded] = useState(false);
  const [activeChat, setActiveChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loadingMessages, setLoadingMessages] = useState(false);

  const messageListRef = useRef(null);
  const user = useSelector((state) => state?.auth?.user);

  const dispatch = useDispatch();
  const isMobile = useIsMobile();

  const conversationId = useSelector((state) => state?.message?.conversationId);

  const location = useLocation();

  const activeChatRef = useRef(null);
  const shouldAutoScrollRef = useRef(true);

  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  const [page, setPage] = useState(1);
  const [hasMoreChats, setHasMoreChats] = useState(true);
  const [loadingChats, setLoadingChats] = useState(false);

  useEffect(() => {
    activeChatRef.current = activeChat;
  }, [activeChat]);

  useEffect(() => {
    if (!chatsLoaded) return;
    if (!location.state?.openChatWith) return;

    OpenChatByUserId(location.state.openChatWith);

    // optional: prevent re-run on back navigation
    window.history.replaceState({}, document.title);
  }, [chatsLoaded, location.state?.openChatWith]);

  useEffect(() => {
    if (!isMobile && activeChat) {
      dispatch(setHideBottomNav(true));
    } else {
      dispatch(setHideBottomNav(false));
    }
  }, [isMobile, activeChat]);

  useEffect(() => {
    const disableInspect = (e) => {
      // F12
      if (e.key === "F12") {
        e.preventDefault();
      }

      // Ctrl + Shift + I / J / C
      if (
        (e.ctrlKey && e.shiftKey && ["I", "J", "C"].includes(e.key)) ||
        (e.metaKey && e.altKey && ["I", "J", "C"].includes(e.key))
      ) {
        e.preventDefault();
      }

      // Ctrl + U (view source)
      if (e.ctrlKey && e.key.toLowerCase() === "u") {
        e.preventDefault();
      }
    };

    const disableRightClick = (e) => e.preventDefault();

    document.addEventListener("keydown", disableInspect);
    document.addEventListener("contextmenu", disableRightClick);

    return () => {
      document.removeEventListener("keydown", disableInspect);
      document.removeEventListener("contextmenu", disableRightClick);
    };
  }, []);

  useEffect(() => {
    if (!conversationId || !socket) return;

    const leaveConversation = () => {
      socket.emit(CHAT_EVENTS.CHAT_LEAVE, { conversationId });
    };

    window.addEventListener("beforeunload", leaveConversation);

    return () => {
      leaveConversation();
      window.removeEventListener("beforeunload", leaveConversation);
    };
  }, [conversationId, socket]);

  useEffect(() => {
    fetchChats(1);
  }, []);

  useEffect(() => {
    document.body.classList.add("hide-zoho-chat");
    return () => document.body.classList.remove("hide-zoho-chat");
  }, []);

  const fetchChats = async (pageToLoad = 1) => {
    if (loadingChats || !hasMoreChats) return;

    setLoadingChats(true);

    try {
      const res = await GetChatConversation(pageToLoad, 20);

      const newChats = res?.data?.conversations || [];

      setChats((prev) =>
        pageToLoad === 1 ? newChats : [...prev, ...newChats]
      );

      setHasMoreChats(pageToLoad < res.data.totalPages);
      setPage(pageToLoad);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingChats(false);
      setChatsLoaded(true);
    }
  };

  const selectChat = async (chat) => {
    if (activeChat?.conversationId === chat?.conversationId) return;

    shouldAutoScrollRef.current = true;

    setActiveChat(chat);
    setMessages([]);
    setLoadingMessages(true);

    if (conversationId) {
      console.log("CHAT_LEAVE ", conversationId);
      socket.emit(CHAT_EVENTS.CHAT_LEAVE, { conversationId });
    }

    console.log("CHAT_JOIN ", chat?.remoteUser?._id);
    socket.emit(CHAT_EVENTS.CHAT_JOIN, { receiverId: chat?.remoteUser?._id },
      async (ack) => {
        console.log("Ack ", ack);
        if (!ack?.success) {
          console.error("Chat join rejected", ack);
          setLoadingMessages(false);
          return;
        }

        try {
          const res = await GetChatMessage(chat.conversationId, {
            limit: 20
          });

          const msgs = res?.data || [];

          setMessages(msgs);
          setHasMore(msgs.length === 20);

          dispatch(setRemoteStatus(chat?.remoteUser?.status));
          dispatch(setRemoteId(chat?.remoteUser?._id));
          dispatch(setConversationId(chat?.conversationId));
          dispatch(resetUnreadByConversation(chat?.conversationId));
        } catch (err) {
          console.error(err);
        } finally {
          setLoadingMessages(false);
        }
      }
    );
  };

  const loadOlderMessages = async () => {
    if (!messageListRef.current || !hasMore || loadingMore) return;

    const container = messageListRef.current;
    const prevHeight = container.scrollHeight;

    shouldAutoScrollRef.current = false; // 👈 key line
    setLoadingMore(true);

    try {
      const oldestMessageId = messages[0]?._id;

      const res = await GetChatMessage(activeChat.conversationId, {
        limit: 20,
        beforeMessageId: oldestMessageId,
      });

      const older = res?.data || [];

      if (older.length === 0) {
        setHasMore(false);
        return;
      }

      setMessages((prev) => [...older, ...prev]);

      requestAnimationFrame(() => {
        container.scrollTop =
          container.scrollHeight - prevHeight;
      });

      setHasMore(older.length === 20);
    } finally {
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    if (!socket) return;

    const handler = (msg) => {
      updateConversationLastMessage(msg);

      if (msg.conversationId !== activeChatRef.current?.conversationId) return;

      setMessages((prev) => {
        if (msg.tempId) {
          const index = prev.findIndex((m) => m.tempId === msg.tempId);
          if (index !== -1) {
            const updated = [...prev];
            updated[index] = msg;
            return updated;
          }
        }

        if (prev.some((m) => m._id === msg._id)) return prev;

        return [...prev, msg];
      });
    };

    socket.on(CHAT_EVENTS.CHAT_NEW_MESSAGE, handler);
    return () => socket.off(CHAT_EVENTS.CHAT_NEW_MESSAGE, handler);
  }, []);

  const updateConversationLastMessage = (msg) => {
    setChats((prev) => {
      const index = prev.findIndex(
        (c) => c.conversationId === msg.conversationId
      );

      if (index === -1) return prev;

      const lastMessage = {
        _id: msg._id,
        senderId: msg.senderId,
        text: msg.payload?.text || "",
        kind: msg.kind,
        access: msg.access,
        createdAt: msg.createdAt,
      };

      const updatedChat = {
        ...prev[index],
        lastMessage,
      };

      const updated = [...prev];
      updated.splice(index, 1);
      return [updatedChat, ...updated]; // move to top
    });
  };

  useEffect(() => {
    const container = messageListRef.current;
    if (!container) return;

    if (shouldAutoScrollRef.current) {
      container.scrollTop = container.scrollHeight;
    }
  }, [messages]);

  const OnChatBackClick = () => {
    if (conversationId) {
      socket.emit(CHAT_EVENTS.CHAT_LEAVE, { conversationId });
    }

    dispatch(setConversationId(null));
    setActiveChat(null)
  }

  const OpenChatByUserId = async (receiverId) => {
    setLoadingMessages(true);

    try {
      const res = await GetOrCreateConversation(receiverId);

      const conversation = Array.isArray(res.data)
        ? res.data[0]
        : res.data;

      if (!conversation) return;

      setChats((prev) => {
        const filtered = prev.filter(
          (c) => c.remoteUser?._id !== conversation.remoteUser?._id
        );
        return [conversation, ...filtered];
      });

      if (activeChat?.conversationId !== conversation.conversationId) {
        await selectChat(conversation);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingMessages(false);
    }
  };

  return (
    <div className="pt-[80px] bg-black h-[100dvh] flex overflow-hidden">
      <div
        className={`${activeChat ? "hidden sm:block" : "block"
          } w-full sm:w-80`}
      >
        <div className="h-[1px] bg-gray-800 w-full shrink-0" />

        <Conversation
          chats={chats}
          activeChat={activeChat}
          onSelectChat={selectChat}
          loadMore={fetchChats}
          hasMore={hasMoreChats}
          loading={loadingChats}
          page={page}
        />
      </div>

      <div
        className={`${activeChat ? "block" : "hidden sm:block"
          } flex-1 min-h-0`}
      >
        <ChatWindow
          activeChat={activeChat}
          messages={messages}
          setMessages={setMessages}
          loadingMessages={loadingMessages}
          messageListRef={messageListRef}
          user={user}
          onBack={OnChatBackClick}
          loadOlderMessages={loadOlderMessages}
          hasMore={hasMore}
          loadingMore={loadingMore}
        />
      </div>
    </div>
  );
}